import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Card from "../components/Card";
import Button from "../components/Button";
import { UserIcon, ChartIcon, WalletIcon } from "../components/Icons";
import { mlmAPI } from "../services/api";

const MLMTeam = () => {
  const navigate = useNavigate();
  const [expandedMembers, setExpandedMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [teamStats, setTeamStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    totalVolume: 0,
    pendingBonus: 0,
  });
  const [teamAggregates, setTeamAggregates] = useState({
    totalTeamDeposits: 0,
    totalTeamRevenue: 0,
    totalTeamPrincipal: 0,
  });
  const [levelWiseData, setLevelWiseData] = useState([]);

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch stats and level-wise referrals in parallel
      const [statsResponse, levelWiseResponse, aggResponse] = await Promise.all([
        mlmAPI.getStats(),
        mlmAPI.getLevelWiseReferrals(20),
        mlmAPI.getTeamAggregates().catch(() => null),
      ]);

      const stats = statsResponse || {};
      setTeamStats({
        totalMembers: Number(stats.totalReferrals || 0),
        activeMembers: Number(stats.activeReferrals || 0),
        totalVolume: Number(stats.totalEarnings || 0),
        pendingBonus: 0, // Backend does not provide this metric in /mlm/stats
      });

      if (aggResponse) {
        setTeamAggregates({
          totalTeamDeposits: Number(aggResponse.totalTeamDeposits || 0),
          totalTeamRevenue: Number(aggResponse.totalTeamRevenue || 0),
          totalTeamPrincipal: Number(aggResponse.totalTeamPrincipal || 0),
        });
      }

      // Flatten level-wise data into single array with level info
      const allMembers = [];
      Object.entries(levelWiseResponse || {}).forEach(([level, members]) => {
        const levelNum = parseInt(String(level).replace("level", ""), 10);
        (members || []).forEach((member) => {
          allMembers.push({
            ...member,
            level: Number.isNaN(levelNum) ? 0 : levelNum,
            volume: `$${Number(member.totalEarnings || 0).toFixed(2)}`,
            downline: Number(member.downlineCount || 0),
            status:
              member.isActive === false
                ? "Inactive"
                : member.status || "Active",
          });
        });
      });
      setLevelWiseData(allMembers);
    } catch (err) {
      console.error("Error fetching team data:", err);
      console.error("Error response:", err.response);
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to load team data. Please make sure you are logged in.";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const toggleMemberExpansion = (id) => {
    setExpandedMembers((prev) =>
      prev.includes(id)
        ? prev.filter((memberId) => memberId !== id)
        : [...prev, id],
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[var(--bg-main)]">
        <Sidebar />
        <div className="flex-1 p-4 pt-16 md:p-8 md:pt-8 md:ml-64">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen bg-[var(--bg-main)]">
        <Sidebar />
        <div className="flex-1 p-4 pt-16 md:p-8 md:pt-8 md:ml-64">
          <Card className="text-center py-12">
            <div className="text-red-400 mb-4">
              <svg
                className="w-16 h-16 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-lg font-semibold">{error}</p>
            </div>
            <Button onClick={fetchTeamData}>Retry</Button>
          </Card>
        </div>
      </div>
    );
  }

  const teamStatsDisplay = [
    {
      icon: <UserIcon className="w-6 h-6" />,
      label: "Total Team Members",
      value: teamStats.totalMembers.toString(),
      change: null,
      changeType: "positive",
    },
    {
      icon: <UserIcon className="w-6 h-6" />,
      label: "Active Members",
      value: teamStats.activeMembers.toString(),
      change: null,
      changeType: "positive",
    },
    {
      icon: <ChartIcon className="w-6 h-6" />,
      label: "Total Team Volume",
      value: `$${teamStats.totalVolume.toFixed(2)}`,
      change: null,
      changeType: "positive",
    },
    {
      icon: <WalletIcon className="w-6 h-6" />,
      label: "Pending Bonus",
      value: `$${teamStats.pendingBonus.toFixed(2)}`,
      change: null,
      changeType: "neutral",
    },
  ];

  const filteredMembers =
    selectedLevel === "all"
      ? levelWiseData
      : levelWiseData.filter((member) => member.level === Number(selectedLevel));

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)]">
      <Sidebar />
      <div className="flex-1 p-4 pt-16 md:p-8 md:pt-8 md:ml-64">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Team Structure
          </h1>
          <p className="text-[var(--text-primary)] text-sm md:text-base">
            Your team to level 20 and downline performance
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <p className="text-sm text-[var(--text-primary)] opacity-70">Total Team Deposits</p>
            <p className="text-2xl font-bold text-[var(--text-primary)]">
              ${teamAggregates.totalTeamDeposits.toFixed(2)}
            </p>
          </Card>
          <Card>
            <p className="text-sm text-[var(--text-primary)] opacity-70">Total Team Revenue</p>
            <p className="text-2xl font-bold text-[var(--text-primary)]">
              ${teamAggregates.totalTeamRevenue.toFixed(2)}
            </p>
          </Card>
          <Card>
            <p className="text-sm text-[var(--text-primary)] opacity-70">Total Team Principal</p>
            <p className="text-2xl font-bold text-[var(--text-primary)]">
              ${teamAggregates.totalTeamPrincipal.toFixed(2)}
            </p>
          </Card>
        </div>

        {/* Team Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {teamStatsDisplay.map((stat, index) => (
            <Card key={index}>
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#22c55e]/20 to-[#16a34a]/20 flex items-center justify-center">
                  <div className="bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                    {stat.icon}
                  </div>
                </div>
                <span
                  className={`text-xs px-2 py-1 rounded-lg ${
                    stat.changeType === "positive"
                      ? "bg-green-500/20 text-green-400"
                      : "bg-[#22c55e]/20 text-[var(--text-primary)]"
                  }`}
                >
                  {stat.change}
                </span>
              </div>
              <p className="text-[var(--text-primary)] text-sm mb-2">{stat.label}</p>
              <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                {stat.value}
              </p>
            </Card>
          ))}
        </div>

        {/* Filters Card */}
        <Card className="mb-8">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Team Level
              </label>
              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-[var(--bg-main)] border border-primary/20 text-[var(--text-primary)] focus:outline-none focus:border-primary"
              >
                <option value="all">All Levels</option>
                {Array.from({ length: 20 }, (_, idx) => idx + 1).map((level) => (
                  <option key={level} value={level}>
                    Level {level}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Date From
              </label>
              <input
                type="date"
                className="w-full px-4 py-3 rounded-xl bg-[var(--bg-main)] border border-primary/20 text-[var(--text-primary)] focus:outline-none focus:border-primary"
                defaultValue="2024-01-01"
              />
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Date To
              </label>
              <input
                type="date"
                className="w-full px-4 py-3 rounded-xl bg-[var(--bg-main)] border border-primary/20 text-[var(--text-primary)] focus:outline-none focus:border-primary"
                defaultValue="2024-12-31"
              />
            </div>

            <Button className="md:w-auto">Apply Filters</Button>
          </div>
        </Card>

        {/* Team Members Table */}
        <Card className="mb-8">
          <h2 className="text-xl md:text-2xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Team Members
          </h2>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-primary/20">
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Member
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Join Date
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Volume
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Downline
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="py-8 text-center text-[var(--text-primary)] opacity-60"
                    >
                      No team members found
                    </td>
                  </tr>
                ) : (
                  filteredMembers.map((member) => (
                      <tr
                        key={member.id}
                        className="border-b border-primary/10 last:border-0 hover:bg-[#22c55e]/5 cursor-pointer transition-all"
                        onClick={() =>
                          navigate(`/team-member-details/${member.id}`)
                        }
                      >
                        <td className="py-4 px-4">
                          <div>
                            <p className="font-medium text-[var(--text-primary)]">
                              {member.name}
                            </p>
                            <p className="text-sm text-[var(--text-primary)] opacity-70">
                              {member.email}
                            </p>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-[var(--text-primary)]">
                          {new Date(member.createdAt).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-4">
                          <span className="font-semibold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                            {member.volume}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-[var(--text-primary)]">
                          {member.downline} members
                        </td>
                        <td className="py-4 px-4">
                          <span
                            className={`px-3 py-1 rounded-lg text-xs font-medium ${
                              member.status === "Active"
                                ? "bg-green-500/20 text-green-400"
                                : "bg-red-500/20 text-red-400"
                            }`}
                          >
                            {member.status}
                          </span>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {filteredMembers.length === 0 ? (
              <p className="text-center text-[var(--text-primary)] opacity-60 py-8">
                No team members found
              </p>
            ) : (
              filteredMembers.map((member) => (
                  <div
                    key={member.id}
                    className="p-4 rounded-xl bg-[var(--bg-card)] border border-primary/10 cursor-pointer hover:border-primary/30 transition-all"
                    onClick={() =>
                      navigate(`/team-member-details/${member.id}`)
                    }
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-medium text-[var(--text-primary)]">{member.name}</p>
                        <p className="text-sm text-[var(--text-primary)] opacity-70">
                          {member.email}
                        </p>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-lg text-xs font-medium ${
                          member.status === "Active"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-red-500/20 text-red-400"
                        }`}
                      >
                        {member.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-[var(--text-primary)] opacity-70 mb-1">Join Date</p>
                        <p className="text-[var(--text-primary)]">
                          {new Date(member.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-[var(--text-primary)] opacity-70 mb-1">Volume</p>
                        <p className="font-semibold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                          {member.volume}
                        </p>
                      </div>
                      <div>
                        <p className="text-[var(--text-primary)] opacity-70 mb-1">Downline</p>
                        <p className="text-[var(--text-primary)]">{member.downline} members</p>
                      </div>
                    </div>
                  </div>
                ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MLMTeam;
